import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { useCallback, useMemo } from 'react';

/**
 * Centralizes PostHog events for the fragments view domain.
 * Exposes semantic actions so callers avoid duplicating event names and payload keys.
 */
export function useFragmentsViewCapture() {
  const capture = usePostHogCapture({ domain: 'fragments_view' });

  const deleteAction = useCallback(
    ({ fragmentId, stationSlug }: { fragmentId: string; stationSlug: string }) => {
      capture('delete_fragment', {
        fragment_id: fragmentId,
        station_slug: stationSlug,
      });
    },
    [capture],
  );

  const clone = useCallback(
    ({ fragmentId, clonedFragmentId }: { fragmentId: string; clonedFragmentId: string }) => {
      capture('clone_fragment', {
        fragment_id: fragmentId,
        cloned_fragment_id: clonedFragmentId,
      });
    },
    [capture],
  );

  const goToStreamFromStart = useCallback(
    ({ stationSlug, fragmentFrom }: { stationSlug: string; fragmentFrom: string }) => {
      capture('go_to_stream_from_start', {
        station_slug: stationSlug,
        fragment_from: fragmentFrom,
      });
    },
    [capture],
  );

  const goToStreamFromPlayback = useCallback(
    ({ stationSlug, currentTs }: { stationSlug: string; currentTs?: number | null }) => {
      capture('go_to_stream_from_playback', {
        station_slug: stationSlug,
        current_ts: currentTs,
      });
    },
    [capture],
  );

  const transcriptionSearch = useCallback(
    ({ query }: { query: string }) => {
      const trimmedQuery = query.trim();
      capture('transcription_search', {
        query_length: trimmedQuery.length,
        has_query: trimmedQuery.length > 0,
      });
    },
    [capture],
  );

  const transcriptionSyncToggle = useCallback(
    ({ enabled }: { enabled: boolean }) => {
      capture('transcription_sync_toggle', { enabled });
    },
    [capture],
  );

  const transcriptionWordEfficiencyToggle = useCallback(
    ({ enabled }: { enabled: boolean }) => {
      capture('transcription_word_efficiency_toggle', { enabled });
    },
    [capture],
  );

  const transcriptionWordClick = useCallback(
    ({ wordStartSeconds, chunkTime }: { wordStartSeconds: number; chunkTime: string }) => {
      capture('transcription_word_click', {
        word_start_seconds: wordStartSeconds,
        chunk_time: chunkTime,
      });
    },
    [capture],
  );

  const transcriptionEditModeToggle = useCallback(
    ({ enabled }: { enabled: boolean }) => {
      capture('transcription_edit_mode_toggle', { enabled });
    },
    [capture],
  );

  const transcriptionWordEditStart = useCallback(
    ({
      wordStartSeconds,
      originalTextLength,
    }: {
      wordStartSeconds: number;
      originalTextLength: number;
    }) => {
      capture('transcription_word_edit_start', {
        word_start_seconds: wordStartSeconds,
        original_text_length: originalTextLength,
      });
    },
    [capture],
  );

  const transcriptionWordEditConfirm = useCallback(
    ({
      wordStartSeconds,
      textChanged,
      originalTextLength,
      newTextLength,
    }: {
      wordStartSeconds: number;
      textChanged: boolean;
      originalTextLength: number;
      newTextLength: number;
    }) => {
      capture('transcription_word_edit_confirm', {
        word_start_seconds: wordStartSeconds,
        text_changed: textChanged,
        original_text_length: originalTextLength,
        new_text_length: newTextLength,
      });
    },
    [capture],
  );

  const transcriptionWordEditCancel = useCallback(
    ({
      wordStartSeconds,
      textChanged,
      originalTextLength,
      draftTextLength,
    }: {
      wordStartSeconds: number;
      textChanged: boolean;
      originalTextLength: number;
      draftTextLength: number;
    }) => {
      capture('transcription_word_edit_cancel', {
        word_start_seconds: wordStartSeconds,
        text_changed: textChanged,
        original_text_length: originalTextLength,
        draft_text_length: draftTextLength,
      });
    },
    [capture],
  );

  const downloadMedia = useCallback(
    ({ mediaType }: { mediaType: 'AUDIO' | 'VIDEO' }) => {
      capture('download_media', { media_type: mediaType });
    },
    [capture],
  );

  const downloadSubtitles = useCallback(
    ({ format }: { format: 'txt' | 'srt' | 'json' }) => {
      capture('download_subtitles', { subtitle_format: format });
    },
    [capture],
  );

  return useMemo(
    () => ({
      delete: deleteAction,
      clone,
      goToStreamFromStart,
      goToStreamFromPlayback,
      transcriptionSearch,
      transcriptionSyncToggle,
      transcriptionWordEfficiencyToggle,
      transcriptionWordClick,
      transcriptionEditModeToggle,
      transcriptionWordEditStart,
      transcriptionWordEditConfirm,
      transcriptionWordEditCancel,
      downloadMedia,
      downloadSubtitles,
    }),
    [
      deleteAction,
      clone,
      goToStreamFromStart,
      goToStreamFromPlayback,
      transcriptionSearch,
      transcriptionSyncToggle,
      transcriptionWordEfficiencyToggle,
      transcriptionWordClick,
      transcriptionEditModeToggle,
      transcriptionWordEditStart,
      transcriptionWordEditConfirm,
      transcriptionWordEditCancel,
      downloadMedia,
      downloadSubtitles,
    ],
  );
}
